import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, TYPE } from "../../utils/designTokens";

// Pieces shared by the restaurant list's sections (screens/Restaurants).

export const LINK_SX = {
  ...FOCUS_RING,
  display: "inline-flex",
  alignItems: "center",
  minHeight: 40,
  px: 0.5,
  borderRadius: "8px",
  fontFamily: FONT_BODY,
  fontSize: 14,
  fontWeight: 600,
  color: C.redLink,
  textDecoration: "none",
  whiteSpace: "nowrap",
  "&:hover": { textDecoration: "underline" },
};

/** 44px round previous / next button for a sideways row. */
export function ArrowButton({ dir, label, onClick, disabled }) {
  const Icon = dir < 0 ? ChevronLeftRoundedIcon : ChevronRightRoundedIcon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      sx={{
        ...BUTTON_RESET,
        ...FOCUS_RING,
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: "50%",
        border: `1px solid ${C.borderStrong}`,
        bgcolor: C.bg,
        color: C.ink,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color .15s ease, opacity .15s ease",
        "&:hover:not(:disabled)": { bgcolor: C.surface },
        "&:disabled": { opacity: 0.35, cursor: "default" },
      }}
    >
      <Icon aria-hidden="true" />
    </Box>
  );
}

/**
 * A sideways-scrolling row: the scroller's ref, previous / next handlers with
 * whether each way can move, and a keydown handler that moves focus between
 * the row's items with ← / → (each item marks its focusable with
 * data-row-item).
 */
export function useRowScroller() {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ canPrev: false, canNext: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const canPrev = el.scrollLeft > 4;
    const canNext = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setEdges((prev) => (prev.canPrev === canPrev && prev.canNext === canNext ? prev : { canPrev, canNext }));
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  });

  const scrollBy = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };

  const onKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const items = [...(ref.current?.querySelectorAll("[data-row-item]") || [])];
    const i = items.indexOf(document.activeElement);
    if (i < 0) return;
    const next = items[i + (e.key === "ArrowRight" ? 1 : -1)];
    if (!next) return;
    e.preventDefault();
    next.focus();
    next.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  };

  return { ref, prev: () => scrollBy(-1), next: () => scrollBy(1), onKeyDown, ...edges };
}

export const ROW_SX = {
  display: "flex",
  m: 0,
  p: 0,
  listStyle: "none",
  overflowX: "auto",
  scrollSnapType: "x proximity",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  // Room for focus rings and selected rings, which would otherwise be clipped.
  py: "4px",
  mx: "-4px",
  px: "4px",
};

/**
 * Section heading: Bricolage title (h2), an optional muted line under it, and
 * on the right a link and / or the row's arrows.
 */
export function SectionHead({ id, title, sub, after, link, row, rowLabel = "items" }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Box component="h2" id={id} sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>
          {title}
          {after}
        </Box>
        {sub && (
          <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.5, color: C.muted }}>
            {sub}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {link && (
          <Box component={RouterLink} to={link.to} sx={LINK_SX}>
            {link.label}
          </Box>
        )}
        {row && (row.canPrev || row.canNext) && (
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
            <ArrowButton dir={-1} label={`Previous ${rowLabel}`} onClick={row.prev} disabled={!row.canPrev} />
            <ArrowButton dir={1} label={`Next ${rowLabel}`} onClick={row.next} disabled={!row.canNext} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** Filter chip with aria-pressed; selected is filled ink with white text. */
export function FilterChip({ pressed, onClick, children }) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      sx={{
        ...BUTTON_RESET,
        ...FOCUS_RING,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 40,
        px: 2,
        borderRadius: RADIUS.pill,
        fontFamily: FONT_BODY,
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: "nowrap",
        bgcolor: pressed ? C.ink : C.bg,
        color: pressed ? "#FFFFFF" : C.ink,
        boxShadow: pressed ? "none" : `inset 0 0 0 1px ${C.borderStrong}`,
        transition: "background-color .15s ease, color .15s ease",
        "&:hover": { bgcolor: pressed ? C.ink : C.surface },
        "& svg": { fontSize: 18 },
      }}
    >
      {children}
    </Box>
  );
}
